/**
 * The notices to the team live in `shared/` (contract v10.50): the layout shows the bell
 * and the window on every screen, Administración has nothing of its own, and the publish
 * window of a tariff asks whether the email to «Comerciales» can go. A feature may import
 * from `shared/` and never from another feature.
 */
import { api, type Page } from '@/shared/api/client';

import type {
  Notice,
  NoticeAudienceOptions,
  NoticeWrite,
  PendingNotices,
  SalespeopleStatus,
} from './types';

/** One page of the bell. */
export const NOTICES_PAGE = 20;

export const noticeKeys = {
  all: ['notices'] as const,
  pending: ['notices', 'pending'] as const,
  list: (limit: number) => ['notices', 'list', limit] as const,
  salespeople: ['notices', 'salespeople'] as const,
  audience: ['notices', 'audience'] as const,
  /** The ids marked seen from this tab, kept in the cache and never asked for: see
   * `useMarkNoticesSeen`. Logging out clears it with everything else. */
  seenHere: ['notices', 'seen-here'] as const,
};

export const noticesApi = {
  /** What the window shows on coming in, and the number on the bell. Marks nothing. */
  pending: (): Promise<PendingNotices> => api.get<PendingNotices>('/api/notices/pending'),
  /** The bell: every notice not withdrawn, newest first, with when this person saw it. */
  list: (limit: number = NOTICES_PAGE): Promise<Page<Notice>> =>
    api.get<Page<Notice>>('/api/notices', { limit }),
  /** «Entendido». Idempotent. */
  seen: (ids: string[]): Promise<void> => api.post<void>('/api/notices/seen', { ids }),
  /** Whoever administers. */
  create: (payload: NoticeWrite): Promise<Notice> => api.post<Notice>('/api/notices', payload),
  /** Whom a notice may be written for (contract v10.55): every profile, and the active
   * people by name. Whoever administers. */
  audience: (): Promise<NoticeAudienceOptions> =>
    api.get<NoticeAudienceOptions>('/api/notices/audience'),
  withdraw: (id: string): Promise<void> => api.post<void>(`/api/notices/${id}/withdraw`),
  /** Whether a tariff's notice can also go by email to «Comerciales», and from where. */
  salespeople: (): Promise<SalespeopleStatus> =>
    api.get<SalespeopleStatus>('/api/notices/salespeople'),
};
